import { Grid, Typography, styled } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { BarLoader } from "react-spinners";
import logo from "../../authenticx-green.png";
import AcxButton from "../../components/UI/AcxButton";
import theme from "../../Theme/AppTheme";
import { useResizeObserver } from "../../utils/useResizeObserver";

const FocusNavBarStyled = styled(Grid)(({ theme }) => ({
    height: 64,
    backgroundColor: theme.palette.primary.main,
    zIndex: 1201, // fallback for theme.zIndex.drawer + 1
    position: "relative",
}));

const HorizontalSpacingImg = styled("img")(({ theme }) => ({
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    height: 30,
    cursor: "pointer",
}));

type Props = {
    title: string;
    onCancelClick?: () => void;
    onSaveClick?: () => Promise<void>;
    onSaveAsClick?: () => void;
    showSave?: boolean;
    showSaveAs?: boolean;
    saveAsText?: string;
    saveText?: string;
    cancelText?: string;
    hideCancelButton?: boolean;
    loading?: boolean;
    saveTooltip?: string;
    saveAsTooltip?: string;
};

function FocusNavBar(props: Props) {
    const navigate = useNavigate();
    const { theRef } = useResizeObserver<HTMLDivElement>({
        name: "focusPageNavToolbar",
        pixelDelta: 1,
        debounceWait: 50,
    });

    function renderCancelButton() {
        if (props.onCancelClick) {
            return (
                <Grid item key={"focus-save-button"} alignContent={"center"}>
                    <AcxButton
                        sx={{
                            backgroundColor: "#5d83dd",
                            "&:hover": {
                                backgroundColor: "rgb(30, 64, 175)",
                            },
                        }}
                        onClick={props.onCancelClick}
                    >
                        {props.cancelText ?? "Done"}
                    </AcxButton>
                </Grid>
            );
        }
    }

    function renderSaveButton() {
        if (props.onSaveClick && props.showSave) {
            return (
                <Grid
                    item
                    key={"save-button"}
                    alignContent={"center"}
                    marginLeft={"10px"}
                >
                    <AcxButton
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                        fullWidth
                        color="white"
                        loading={props.loading}
                        onClick={props.onSaveClick}
                        tooltip={
                            props.saveTooltip ?? "Save changes to this report"
                        }
                    >
                        {props.saveText ?? "Save"}
                    </AcxButton>
                </Grid>
            );
        }
        return false;
    }

    function renderSaveAsFilterButton() {
        if (props.onSaveAsClick && props.showSaveAs) {
            return (
                <Grid
                    item
                    key={"save-as-button"}
                    alignContent={"center"}
                    marginLeft={"10px"}
                >
                    <AcxButton
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                        tooltip={
                            props.saveAsTooltip ??
                            "Save new report from existing"
                        }
                        color="white"
                        size={"large"}
                        onClick={props.onSaveAsClick}
                    >
                        {props.saveAsText ?? "Save As"}
                    </AcxButton>
                </Grid>
            );
        }
        return false;
    }

    const onLogoClick = () => {
        navigate(-1);
    };

    return (
        <FocusNavBarStyled
            ref={theRef}
            container
            justifyContent="space-between"
            alignItems="center"
            wrap={"nowrap"}
        >
            <Grid container item xs={6} wrap={"nowrap"} alignItems={"stretch"}>
                <Grid item>
                    <HorizontalSpacingImg
                        alt=""
                        style={{ marginRight: "20px" }}
                        onClick={onLogoClick}
                        src={logo}
                    />
                </Grid>
                <Grid item zeroMinWidth>
                    <Typography
                        sx={{
                            color: theme.palette.white.main,
                            fontSize: 20,
                            lineHeight: "28px",
                        }}
                    >
                        {props.title}
                    </Typography>
                    <BarLoader
                        color={theme.palette.primary.main}
                        loading={props.loading ?? false}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                item
                xs={6}
                justifyContent={"flex-end"}
                paddingRight={"24px"}
            >
                {[
                    props.hideCancelButton ? null : renderCancelButton(),
                    props.showSaveAs ? renderSaveAsFilterButton() : null,
                    renderSaveButton(),
                ]}
            </Grid>
        </FocusNavBarStyled>
    );
}

export default FocusNavBar;
